// XAUUSD Elliott Wave — geometrija grafikona, verbatim port dizajn-prototipa
// (design_handoff_xauusd_poll/WaveChart.dc.html). Sve koordinate su u fiksnom
// 1800x730 prostoru grafikona unutar 1920px platna; platno se skalira kao
// celina (ScaledCanvas), pa je dizajn piksel-precizan na svim širinama.
// Sveće su sintetičke (deterministički PRNG — isti output na serveru i
// klijentu); projekcione strelice i oznake talasa su ručno definisane u
// handoff-u i ne menjaju se.

export const POLL_ID = 'xauusd-2026';

// Lako izmenjivo zaglavlje (README: datum/cenu učiniti dinamičnim ili lako izmenjivim)
export const LAST_PRICE = 4455;
export const LAST_PRICE_LABEL = '4.455';
export const PRICE_DATE_LABEL = '25.08.2026';
export const PRICE_SOURCE = 'ICMarkets';

type Bar = { o: number; c: number; hi: number; lo: number };

export interface Candle {
  x: number; hy: number; ly: number; col: string;
  bx: number; by: number; bw: number; bh: number;
}
export interface GridLine { y: number; top: number; label: string }
export interface MonthTick { t: string; x: number; left: number }
export interface WaveMark {
  x: number; y: number; left: number; align: string;
  t: string; sub: string; lTop: number; sTop: number;
}
export interface PivotPill { left: number; top: number; t: string; col: string }
export interface Zone { x: number; y: number; w: number; h: number; tx: number; tTop: number; label: string }

export interface ChartGeom {
  candles: Candle[]; grid: GridLine[]; months: MonthTick[];
  path: string; head: string; marks: WaveMark[]; pivots: PivotPill[];
  zone: Zone; nowY: number; nowTop: number;
}

interface ProjPoint { n: number; p: number; t: string; sub: string; up?: boolean; align?: string }
interface PivotCfg { i: number; p: number; t: string; up?: boolean; col: string }
interface ChartCfg {
  min: number; max: number; step: number;
  zone: [number, number]; zoneN: [number, number]; zoneLabel: string;
  proj: ProjPoint[]; pivots: PivotCfg[];
}

// Kontrolne tačke koje imitiraju stvarni oblik XAUUSD istorije Okt 2025 – Avg 2026
const CTRL: Array<[number, number]> = [
  [0, 3650], [10, 4180], [20, 3830], [30, 3900], [44, 4090], [52, 4180], [58, 4160],
  [66, 4530], [72, 4560], [80, 4480], [88, 4520], [93, 4400], [96, 4980], [100, 5250],
  [104, 5150], [108, 5450], [114, 5150], [118, 5250], [124, 4900], [128, 4500],
  [130, 4050], [136, 4650], [140, 4835], [146, 4790], [152, 4600], [158, 4700],
  [164, 4500], [170, 4400], [176, 4300], [182, 4180], [188, 3990], [194, 4200],
  [198, 4350], [204, 4250], [210, 4100], [216, 3944], [222, 4080], [228, 4200],
  [232, 4330], [236, 4480], [239, 4690], [240, 4600], [241, 4455],
];

function makeBars(): Bar[] {
  let s = 20260825;
  const rnd = () => { s = (s * 1103515245 + 12345) % 2147483648; return s / 2147483648; };
  const N = 242;
  const closes: number[] = [];
  for (let i = 0; i < N; i++) {
    let k = 0;
    while (k < CTRL.length - 2 && CTRL[k + 1][0] <= i) k++;
    const i0 = CTRL[k][0], p0 = CTRL[k][1], i1 = CTRL[k + 1][0], p1 = CTRL[k + 1][1];
    const t = Math.min(1, Math.max(0, (i - i0) / (i1 - i0)));
    const base = p0 + (p1 - p0) * (t * t * (3 - 2 * t));
    closes.push(base + (rnd() - 0.5) * (Math.abs(p1 - p0) * 0.10 + 26));
  }
  closes[241] = LAST_PRICE;
  const out: Bar[] = [];
  for (let i = 0; i < N; i++) {
    const o = i === 0 ? closes[0] - 15 : closes[i - 1];
    const c = closes[i];
    let hi = Math.max(o, c) + rnd() * 32 + 6;
    let lo = Math.min(o, c) - rnd() * 32 - 6;
    if (i === 93) { hi = 5600; lo = 4340; } // spike bar iz prototipa
    out.push({ o, c, hi, lo });
  }
  return out;
}

const BARS = makeBars();

function chart(cfg: ChartCfg): ChartGeom {
  const bars = BARS, N = bars.length;
  const X0 = 70, HISTW = 1000, RIGHT = 1600, TOP = 40, BOT = 640;
  const bw = HISTW / N;
  const y = (p: number) => TOP + (cfg.max - p) / (cfg.max - cfg.min) * (BOT - TOP);
  const bx = (i: number) => X0 + i * bw + bw / 2;
  const r1 = (v: number) => +v.toFixed(1);

  const candles: Candle[] = bars.map((b, i) => {
    const up = b.c >= b.o, t = y(Math.max(b.o, b.c)), bo = y(Math.min(b.o, b.c));
    return {
      x: r1(bx(i)), hy: r1(y(b.hi)), ly: r1(y(b.lo)), col: up ? '#12A06A' : '#E0483D',
      bx: r1(bx(i) - bw * 0.34), by: r1(t), bw: r1(bw * 0.68), bh: Math.max(1.2, r1(bo - t)),
    };
  });

  const grid: GridLine[] = [];
  for (let p = Math.ceil(cfg.min / cfg.step) * cfg.step; p <= cfg.max; p += cfg.step) {
    grid.push({ y: r1(y(p)), top: r1(y(p) - 9), label: p.toLocaleString('de-DE') });
  }

  const mNames = ['Okt', 'Nov', 'Dec', 'Jan 26', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Avg'];
  const months: MonthTick[] = mNames.map((t, k) => {
    const x = X0 + (k * 22 + 11) * bw;
    return { t, x: r1(x), left: r1(x - 60) };
  });

  const lastX = bx(N - 1), FW = (RIGHT - lastX) / 140;
  const pts = [{ x: lastX, y: y(LAST_PRICE), t: '', sub: '', dy: 0, align: 'center' }].concat(
    cfg.proj.map((p) => ({
      x: r1(lastX + p.n * FW), y: r1(y(p.p)), t: p.t, sub: p.sub,
      dy: p.up ? -44 : 30, align: p.align || 'center',
    }))
  );
  let d = 'M ' + r1(pts[0].x) + ' ' + r1(pts[0].y);
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1], b = pts[i], dx = (b.x - a.x) * 0.42;
    d += ' C ' + r1(a.x + dx) + ' ' + r1(a.y) + ', ' + r1(b.x - dx) + ' ' + r1(b.y) + ', ' + r1(b.x) + ' ' + r1(b.y);
  }
  const e = pts[pts.length - 1], q = pts[pts.length - 2];
  const ang = Math.atan2(e.y - q.y, (e.x - q.x) * 0.5), L = 30, W = 13;
  const head = [
    [e.x + Math.cos(ang) * L, e.y + Math.sin(ang) * L],
    [e.x - Math.sin(ang) * W, e.y + Math.cos(ang) * W],
    [e.x + Math.sin(ang) * W, e.y - Math.cos(ang) * W],
  ].map((p) => r1(p[0]) + ',' + r1(p[1])).join(' ');

  const marks: WaveMark[] = pts.slice(1).map((p) => {
    const up = p.dy < 0;
    const left = p.align === 'right' ? p.x - 236 : p.x - 120;
    return {
      x: p.x, y: p.y, left: r1(left), align: p.align, t: p.t, sub: p.sub,
      lTop: r1(p.y + (up ? -62 : 22)), sTop: r1(p.y + (up ? -28 : 58)),
    };
  });

  const pivots: PivotPill[] = cfg.pivots.map((v) => ({
    left: r1(bx(v.i) - (16 + v.t.length * 5)), top: r1(y(v.p) + (v.up ? -40 : 16)),
    t: v.t, col: v.col,
  }));

  const zTop = y(cfg.zone[1]), zBot = y(cfg.zone[0]);
  const zone: Zone = {
    x: r1(lastX + cfg.zoneN[0] * FW), y: r1(zTop),
    w: r1((cfg.zoneN[1] - cfg.zoneN[0]) * FW), h: r1(zBot - zTop),
    tx: r1(lastX + cfg.zoneN[0] * FW), tTop: r1(zTop - 24), label: cfg.zoneLabel,
  };

  return {
    candles, grid, months, path: d, head, marks, pivots, zone,
    nowY: r1(y(LAST_PRICE)), nowTop: r1(y(LAST_PRICE) - 11),
  };
}

export interface Scenario {
  geom: ChartGeom;
  badge: string;
  color: string;        // boja strelice, badge-a, oznaka talasa
  glow: string;         // glow ispod strelice
  headerBg: string;
  title: string;
  desc: string;
  zoneFill: string;
  zoneStroke: string;
}

export const SCENARIO_A: Scenario = {
  badge: 'A',
  color: '#0E8455',
  glow: 'rgba(14,132,85,0.16)',
  headerBg: '#F1F8F6',
  title: 'Bikovski scenario — impuls se nastavlja',
  desc: '① završen na 4.697 · korekcija ② u zonu 4.194–4.338 · zatim ③ ka 5.600 i ⑤ preko 6.000',
  zoneFill: 'rgba(14,132,85,0.10)',
  zoneStroke: 'rgba(14,132,85,0.45)',
  geom: chart({
    min: 3600, max: 6200, step: 300,
    zone: [4194, 4338], zoneN: [8, 36], zoneLabel: 'Zona kupovine 4.194 — 4.338',
    proj: [
      { n: 22, p: 4260, t: '②', sub: '4.194 — 4.338' },
      { n: 74, p: 5600, t: '③', sub: '5.550 — 5.650', up: true },
      { n: 100, p: 5180, t: '④', sub: '~5.200' },
      { n: 138, p: 6100, t: '⑤', sub: '6.100+', up: true, align: 'right' },
    ],
    pivots: [
      { i: 108, p: 5450, t: '((I))', up: true, col: '#0E7C86' },
      { i: 130, p: 4050, t: 'A', col: '#5C6B78' },
      { i: 140, p: 4835, t: 'B', up: true, col: '#5C6B78' },
      { i: 216, p: 3944, t: '((II))', col: '#0E7C86' },
      { i: 240, p: 4697, t: '①', up: true, col: '#0E8455' },
    ],
  }),
};

export const SCENARIO_B: Scenario = {
  badge: 'B',
  color: '#C8362A',
  glow: 'rgba(200,54,42,0.14)',
  headerBg: '#FDF3F2',
  title: 'Medveđi scenario — rast je bio samo korekcija',
  desc: '4.697 je ②(C) · sledi ③ ka zoni 3.338–3.541 · odbijanje ④ ka 3.950 i ⑤ ka 3.200',
  zoneFill: 'rgba(200,54,42,0.09)',
  zoneStroke: 'rgba(200,54,42,0.42)',
  geom: chart({
    min: 3050, max: 5700, step: 300,
    zone: [3338, 3541], zoneN: [48, 94], zoneLabel: 'Ciljna zona ③  3.338 — 3.541',
    proj: [
      { n: 70, p: 3450, t: '③', sub: '3.338 — 3.541' },
      { n: 108, p: 3950, t: '④', sub: '~3.950', up: true },
      { n: 140, p: 3200, t: '⑤', sub: '3.200', align: 'right' },
    ],
    pivots: [
      { i: 108, p: 5450, t: '((I))', up: true, col: '#0E7C86' },
      { i: 130, p: 4050, t: 'A', col: '#5C6B78' },
      { i: 140, p: 4835, t: 'B', up: true, col: '#5C6B78' },
      { i: 216, p: 3944, t: '①', col: '#C8362A' },
      { i: 240, p: 4697, t: '②(C)', up: true, col: '#C8362A' },
    ],
  }),
};
